import React, { useState, useEffect, useContext, useRef } from "react"
import moment from "moment"
import algoliasearch from "algoliasearch"

import { AlertsContext } from "../../../utils/providers/alerts"
import { db, funEU } from "../../../utils/firebase"

import Window from "../../structure/window/window"
import WindowCard from "../../ui/window-card/window-card"
import Input from "../../ui/inputs/input"
import Textarea from "../../ui/inputs/textarea"
import Button from "../../ui/button/button"
import { LoadingIcon } from "../../../utils/svgs"

import styles from "./create-client-payment-link.module.scss"

const createClientPaymentLinkCallable = funEU.httpsCallable("createClientPaymentLink")

// Same keys used everywhere else in the app for Algolia.
const algoliaClient = algoliasearch("HY764WYKNV", "059a9cd79a33ec2b2822acf2ffdfd4c9")
const clientsIndex = algoliaClient.initIndex("clients")

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

/**
 * Modal for creating a standalone (client-level, no booking) Stripe Payment
 * Link. Two-stage UX: client picker first (Algolia search with 3-char
 * minimum, debounced), then once a client is chosen the summary card
 * unlocks the amount + note + send fields.
 */
export default function CreateClientPaymentLink({ close, onCreated }) {
	const { pushAlert } = useContext(AlertsContext)

	// Search state
	const [query, setQuery] = useState("")
	const [searching, setSearching] = useState(false)
	const [results, setResults] = useState([])
	const [searchOpen, setSearchOpen] = useState(false)

	// Selected client + summary state
	const [selectedClient, setSelectedClient] = useState(null)
	const [summary, setSummary] = useState(null)

	// Payment fields
	const [payee, setPayee] = useState("")
	const [amount, setAmount] = useState("")
	const [note, setNote] = useState("")
	const [alternateEmail, setAlternateEmail] = useState("")
	const [sending, setSending] = useState(false)
	const [errors, setErrors] = useState({})

	const debounceTimer = useRef(null)

	// Debounced Algolia search on every keystroke past the 3-char minimum.
	useEffect(() => {
		if (debounceTimer.current) clearTimeout(debounceTimer.current)

		if (selectedClient) return // search panel hidden once a client is chosen
		const trimmed = query.trim()
		if (trimmed.length < 3) {
			setResults([])
			setSearching(false)
			return
		}

		setSearching(true)
		debounceTimer.current = setTimeout(async () => {
			try {
				const response = await clientsIndex.search(trimmed, { hitsPerPage: 8 })
				setResults(response.hits || [])
				setSearchOpen(true)
			} catch (err) {
				console.error("Algolia search failed:", err)
				setResults([])
			} finally {
				setSearching(false)
			}
		}, 250)

		return () => {
			if (debounceTimer.current) clearTimeout(debounceTimer.current)
		}
	}, [query, selectedClient])

	const handleSelectClient = async (hit) => {
		const clientID = hit.objectID
		setSelectedClient({ id: clientID, ...hit })
		setSearchOpen(false)
		setResults([])
		// Default the payee to the client's full name; staff can change it.
		const fullName = [hit.first_name, hit.last_name].filter(Boolean).join(" ")
		setPayee(fullName)

		// Seed the summary immediately from the Algolia hit so the card has
		// something to show even before the live Firestore fetch resolves —
		// or if the counts fetch fails altogether.
		setSummary({
			first_name: hit.first_name,
			last_name: hit.last_name,
			email: hit.email,
			phone: hit.phone,
			stableford_points: 0,
			openEnquiries: null,
			activeBookings: null,
			pastBookings: null,
			clientSince: "—",
		})

		const today = moment().startOf("day").valueOf()

		try {
			// Single-field queries only — avoids needing composite indexes for
			// the various date filters. We slice active/past in JS below.
			const [clientSnap, enquiriesSnap, bookingsSnap] = await Promise.all([
				db.doc(`clients/${clientID}`).get(),
				db.collection("enquiries").where("client", "==", clientID).get(),
				db.collection("bookings").where("client", "==", clientID).get(),
			])

			const client = clientSnap.data() || {}

			const openEnquiries = enquiriesSnap.docs.filter((d) => {
				const data = d.data() || {}
				return !data.removed && !data.converted && data.status !== "ARCHIVED"
			}).length

			let activeBookings = 0
			let pastBookings = 0
			bookingsSnap.docs.forEach((d) => {
				const data = d.data() || {}
				if (data.removed) return
				const dateFromMs = data.date_from?.toMillis?.() || (data.date_from?.seconds ? data.date_from.seconds * 1000 : null)
				if (dateFromMs && dateFromMs >= today) activeBookings += 1
				else pastBookings += 1
			})

			const createdMillis = client.created?.toMillis?.() || (client.created?._seconds ? client.created._seconds * 1000 : null)

			setSummary({
				first_name: client.first_name || hit.first_name,
				last_name: client.last_name || hit.last_name,
				email: client.email || hit.email,
				phone: client.phone || hit.phone,
				stableford_points: Number(client.stableford_points || 0),
				openEnquiries,
				activeBookings,
				pastBookings,
				clientSince: createdMillis ? moment(createdMillis).format("DD MMM YYYY") : "—",
			})
		} catch (err) {
			console.error("Client summary fetch failed:", err)
			// Algolia-only summary already seeded above — leave it as-is.
		}
	}

	const clearSelection = () => {
		setSelectedClient(null)
		setSummary(null)
		setQuery("")
		setPayee("")
		setAmount("")
		setNote("")
		setAlternateEmail("")
		setErrors({})
	}

	const handleAmountChange = (raw) => {
		if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) {
			setAmount(raw)
		}
	}

	const validate = () => {
		const next = {}
		if (!selectedClient) next.client = "Select a client first."
		if (!payee.trim()) next.payee = "Payee name is required."

		const amountNum = Number(amount)
		if (!amount.trim()) next.amount = "Amount is required."
		else if (!Number.isFinite(amountNum)) next.amount = "Amount must be a number."
		else if (amountNum <= 0) next.amount = "Amount must be greater than zero."

		// Client-level link: recipient defaults to the client's own email.
		// If the client doc has no email, staff supplies an alternate inline.
		if (!summary?.email) {
			const trimmed = alternateEmail.trim()
			if (!trimmed) next.recipient = "Recipient email is required."
			else if (!EMAIL_RE.test(trimmed)) next.recipient = "That doesn't look like a valid email address."
		}

		if (note.length > 500) next.note = "Note is too long (max 500 characters)."

		setErrors(next)
		return Object.keys(next).length === 0
	}

	const submit = async () => {
		if (!validate()) return

		const useLeadBooker = Boolean(summary?.email)

		setSending(true)
		try {
			await createClientPaymentLinkCallable({
				clientID: selectedClient.id,
				amount: Number(amount),
				payee: payee.trim(),
				note: note.trim() || undefined,
				useLeadBooker,
				alternateEmail: useLeadBooker ? undefined : alternateEmail.trim(),
			})
			pushAlert({ type: "SUCCESS", title: "Payment link sent" })
			if (typeof onCreated === "function") onCreated()
		} catch (err) {
			pushAlert({ type: "ERROR", title: err?.message || "Could not create payment link." })
			setSending(false)
		}
	}

	return (
		<Window
			title="Create payment link"
			className="slim"
			close={() => close()}>
			<WindowCard>
				<div className="input-grid cols-2">
					{/* Stage 1 — client search */}
					{!selectedClient && (
						<div className={["span-2", styles.searchWrap].join(" ")}>
							<label className={styles.label}>Find a client</label>
							<div className={styles.searchInner}>
								<Input
									type="text"
									placeholder="Search by name, email or phone..."
									value={query}
									onChange={setQuery}
								/>
								{searching && (
									<div className={styles.searchSpinner}>
										<LoadingIcon />
									</div>
								)}
							</div>

							{searchOpen && results.length > 0 && (
								<div className={styles.searchResults}>
									{results.map((hit) => (
										<button
											key={hit.objectID}
											type="button"
											className={styles.searchResult}
											onClick={() => handleSelectClient(hit)}>
											<div className={styles.searchResultName}>
												{[hit.first_name, hit.last_name].filter(Boolean).join(" ") || "Unnamed client"}
											</div>
											<div className={styles.searchResultMeta}>
												{hit.email || "—"}
												{hit.phone ? ` · ${hit.phone}` : ""}
											</div>
										</button>
									))}
								</div>
							)}

							{searchOpen && !searching && results.length === 0 && query.trim().length >= 3 && (
								<p className={styles.searchEmpty}>No matches. Try a different name, email or phone.</p>
							)}

							{errors.client && <p className={styles.error}>{errors.client}</p>}
						</div>
					)}

					{/* Stage 2 — selected client + payment fields */}
					{selectedClient && (
						<>
							<div className={["span-2", styles.summaryCard].join(" ")}>
								<div className={styles.summaryTop}>
									<div>
										<div className={styles.summaryName}>{[summary?.first_name, summary?.last_name].filter(Boolean).join(" ") || "Loading..."}</div>
										<div className={styles.summarySub}>
											{summary?.email || "—"}
											{summary?.phone ? ` · ${summary.phone}` : ""}
										</div>
									</div>
									<button
										type="button"
										className={styles.changeClient}
										onClick={clearSelection}>
										Change client
									</button>
								</div>

								{summary && (
									<div className={styles.summaryGrid}>
										<Stat
											label="Stableford"
											value={summary.stableford_points}
										/>
										<Stat
											label="Open enquiries"
											value={summary.openEnquiries}
										/>
										<Stat
											label="Active bookings"
											value={summary.activeBookings}
										/>
										<Stat
											label="Past bookings"
											value={summary.pastBookings}
										/>
										<Stat
											label="Client since"
											value={summary.clientSince}
											wide
										/>
									</div>
								)}
							</div>

							<div className="span-2">
								<Input
									type="text"
									placeholder="Payee name:"
									value={payee}
									onChange={setPayee}
								/>
								{errors.payee && <p className={styles.error}>{errors.payee}</p>}
							</div>

							<div className="span-2">
								<Input
									type="text"
									inputMode="decimal"
									placeholder="Amount:"
									symbol="£"
									value={amount}
									onChange={handleAmountChange}
								/>
								{errors.amount && <p className={styles.error}>{errors.amount}</p>}
							</div>

							<div className="span-2">
								<Textarea
									type="text"
									placeholder="Optional note for the email:"
									value={note}
									onChange={setNote}
								/>
								{errors.note && <p className={styles.error}>{errors.note}</p>}
							</div>

							{/* Client-level link: recipient is always the chosen client.
							    Show a static statement when we have their email; fall back
							    to an inline input if the client doc has no email on file. */}
							<div className="span-2">
								{summary?.email ? (
									<p className={styles.recipientNote}>
										This will be sent to <strong>{summary.email}</strong>.
									</p>
								) : (
									<>
										<p className={styles.recipientWarn}>This client has no email on file. Enter a recipient email below.</p>
										<Input
											type="email"
											placeholder="Recipient email:"
											value={alternateEmail}
											onChange={(v) => {
												setAlternateEmail(v)
												setErrors((e) => ({ ...e, recipient: undefined }))
											}}
										/>
										{errors.recipient && <p className={styles.error}>{errors.recipient}</p>}
									</>
								)}
							</div>

							<div className="span-2">
								<Button
									label={`Create & send payment link${amount ? ` for £${formatGbp(Number(amount) || 0)}` : ""}`}
									fullWidth={true}
									loading={sending}
									loadingText="Sending payment link..."
									onClick={submit}
									disabled={sending}
								/>
							</div>
						</>
					)}
				</div>
			</WindowCard>
		</Window>
	)
}

function Stat({ label, value, wide }) {
	const display = value === null || value === undefined ? "—" : value
	return (
		<div className={[styles.stat, wide ? styles.statWide : ""].join(" ")}>
			<p className={styles.statLabel}>{label}</p>
			<p className={styles.statValue}>{display}</p>
		</div>
	)
}
